import { FC } from 'react';

import { ValidBidAmount } from 'src/api/auctionApi';
import { useAuctionCloseCountDown } from 'src/hooks/Auction/useAuctionCloseCountDown';
import { useAuctionTimeRemaining } from 'src/hooks/Auction/useAuctionTimeRemaining';
import { AuctionUtils } from 'src/pages/Auction/AuctionUtils';
import { DateUtils } from 'src/utils/date.utils';
import { AuctionAlert } from '../AuctionAlert';
import styles from './BidInfo.module.scss';
import { BidResult } from './BidResultEnum';
import { bidResultMap } from './BidResultMap';

export const BidInfo: FC<{
  openSeatCount: number;
  lowestAvailableBid: number;
  closeDate: string;
  bidAmountUsd?: ValidBidAmount;
  bidResult: BidResult | null;
}> = ({
  openSeatCount,
  lowestAvailableBid,
  closeDate,
  bidAmountUsd,
  bidResult,
}) => {
  const isAuctionClosed = useAuctionCloseCountDown(closeDate);
  const auctionTimeRemaining = useAuctionTimeRemaining(closeDate);
  const bidAmount = bidAmountUsd ?? lowestAvailableBid;

  return (
    <div className={styles.container}>
      {!isAuctionClosed && (
        <div
          className={styles.timeRemaining}
          aria-label={`Time remaining: ${auctionTimeRemaining}, ${openSeatCount} seats available`}
        >
          <span>{DateUtils.timeRemaining(closeDate)}</span>
          <span aria-hidden="true">&bull;</span>
          <span>{openSeatCount} seats available</span>
        </div>
      )}

      {isAuctionClosed ? (
        <div className={styles.auctionClosed} role="status">
          Auction has ended
        </div>
      ) : (
        <div
          className={styles.bidMinimum}
          aria-label={`Current minimum bid is ${AuctionUtils.formatDollars(lowestAvailableBid, 0)} per month`}
        >
          Current Minimum Bid:{' '}
          {AuctionUtils.formatDollars(lowestAvailableBid, 0)}/month
        </div>
      )}

      {bidResult && bidAmount && (
        <AuctionAlert type={bidResultMap[bidResult].type}>
          <AuctionAlert.Icon>{bidResultMap[bidResult].icon}</AuctionAlert.Icon>
          <AuctionAlert.Content>
            <p>{bidResultMap[bidResult].message}</p>
            {bidResult !== BidResult.AUCTION_CLOSING && (
              <span className={styles.bidAmount}>
                {AuctionUtils.formatDollars(bidAmount)}
              </span>
            )}
          </AuctionAlert.Content>
        </AuctionAlert>
      )}
    </div>
  );
};
